"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import {
  User,
  Wallet,
  Store,
  ClipboardList,
  Heart,
  MapPin,
  MessageCircle,
  LogOut,
  ChevronLeft,
  ExternalLink,
} from "lucide-react";
import useAuth from "@core/helpers/client/hooks/useAuth";
import { mark_logout_navigation } from "@/helpers/accountAuth";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import { get_user_wallet_query } from "@core/services/checkout";
import PriceComponent from "@/components/ui/Price";
import ModalComponent from "@/components/ui/ModalComponent";
import Button from "@/components/ui/button";
import useSellerPanel, { PANEL_LOGIN_URL } from "@/hooks/useSellerPanel";

const UserDropdown = () => {
  const [open, setOpen] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [user_wallet, set_user_wallet] = useState(0);

  const dropdownRef = useRef(null);
  const { logout_process } = useAuth();
  const { user } = useAuthContext();
  const { web_route_names } = useWebSiteContext();
  const { can_access_panel, open_panel } = useSellerPanel();
  const router = useRouter();

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const get_user_wallet = async () => {
    const result = await get_user_wallet_query();
    if (result?.status) {
      set_user_wallet(result?.data?.sum_money_ballance || 0);
    }
  };

  useEffect(() => {
    if (open) {
      get_user_wallet();
    }
  }, [open]);

  const MenuItem = ({
    href,
    icon: Icon,
    label,
    badge,
    price,
    onClick,
    target,
    isPrimary = false,
  }) => (
    <Link
      href={href || ""}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      className={`flex items-center justify-between p-4 transition-colors border-b border-jmt-neutral-100 ${isPrimary
        ? "text-jmt-primary-800 font-bold"
        : "text-jmt-main hover:text-jmt-primary-800"
        }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <Icon size={20} />

        <span className={`text-sm ${isPrimary ? "font-bold" : ""}`}>
          {label}
        </span>

        {badge && (
          <span className="text-xs font-bold text-jmt-blue-700 bg-jmt-blue-100 px-1 py-0.5 rounded-xl">
            {badge}
          </span>
        )}

        {price && (
          <span className="text-jmt-neutral-500 text-sm">
            {price}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2"> {isPrimary ? (<ExternalLink size={20} />) : (<ChevronLeft size={20} />)} </div>
    </Link>
  );

  const handleOpenLogoutModal = () => {
    setOpen(false);
    setLogoutModal(true);
  };

  const handleLogout = () => {
    mark_logout_navigation();
    logout_process();
    setLogoutModal(false);
    router.replace("/");
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="cursor-pointer flex items-center font-bold text-sm text-jmt-neutral-800 rounded-md border border-jmt-neutral-200 hover:bg-jmt-neutral-100 py-2 px-4 gap-2"
          onClick={() => setOpen((v) => !v)}
        >
          <User size={22} />
          <div className="whitespace-nowrap">
            {user?.full_name || user?.nickname || "نام کاربر"}
          </div>
        </button>

        {open && (
          <div className="absolute left-0 top-[50px] w-[300px] bg-jmt-white rounded-md shadow-medium z-30 overflow-hidden">
            <div className="flex flex-col">
              {can_access_panel && (
                <MenuItem
                  href={PANEL_LOGIN_URL}
                  icon={Store}
                  label="پنل فروشندگان"
                  target="_blank"
                  isPrimary
                  onClick={(event) => {
                    setOpen(false);
                    open_panel(event);
                  }}
                />
              )}
              <MenuItem
                href={web_route_names?.["account-page"]}
                icon={User}
                label="مشاهده حساب کاربری"
                onClick={() => setOpen(false)}
              />
              <MenuItem
                href={web_route_names?.["transactions-page"]}
                icon={Wallet}
                label="کیف پول"
                price={
                  <PriceComponent
                    price={user_wallet}
                    className="text-jmt-neutral-500 text-sm"
                  />
                }
                onClick={() => setOpen(false)}
              />
              <MenuItem
                href={web_route_names?.["orders-page"]}
                icon={ClipboardList}
                label="سفارش‌های من"
                onClick={() => setOpen(false)}
              />
              <MenuItem
                href={web_route_names?.["favorites-page"]}
                icon={Heart}
                label="علاقه‌مندی‌ها"
                onClick={() => setOpen(false)}
              />
              <MenuItem
                href={web_route_names?.["addresses-page"]}
                icon={MapPin}
                label="آدرس‌ها"
                onClick={() => setOpen(false)}
              />
              <MenuItem
                href={web_route_names?.["comments-page"]}
                icon={MessageCircle}
                label="نظرها و پرسش‌ها"
                onClick={() => setOpen(false)}
              />
            </div>

            <button
              type="button"
              onClick={handleOpenLogoutModal}
              className="w-full flex items-center gap-2 p-4 text-jmt-main hover:bg-jmt-red-50 hover:text-jmt-red-600 transition-colors cursor-pointer"
            >
              <LogOut size={20} />
              <span className="text-sm">خروج از حساب کاربری</span>
            </button>
          </div>
        )}
      </div>

      <ModalComponent
        isOpen={logoutModal}
        onClose={() => setLogoutModal(false)}
        title="خروج از حساب کاربری"
        size="sm"
        footerPosition="left"
        footer={
          <div className="flex items-center gap-2 w-full">
            <Button
              text="خروج از حساب"
              color_type="primary"
              className="w-full bg-jmt-red-500! text-jmt-white! border-jmt-red-500!"
              onClick={handleLogout}
            />
            <Button
              text="انصراف"
              color_type="primary"
              variant="outlined"
              className="w-full"
              onClick={() => setLogoutModal(false)}
            />
          </div>
        }
      >
        <p className="text-sm">
          آیا مطمئن هستید که می‌خواهید از حساب کاربری خود خارج شوید؟ برای استفاده مجدد از امکانات حساب و سبد خرید فعلی، کافی است دوباره وارد شوید.        </p>
      </ModalComponent>
    </>
  );
};

export default UserDropdown;
